import React, { useEffect, useState } from 'react';
interface MoodSphereProps {
  onCapture?: (mood: {
    x: number;
    y: number;
    label: string;
  }) => void;
  size?: 'small' | 'medium' | 'large';
  initialPosition?: {
    x: number;
    y: number;
  };
  showLabels?: boolean;
}
const MoodSphere: React.FC<MoodSphereProps> = ({
  onCapture,
  size = 'medium',
  initialPosition = {
    x: 50,
    y: 50
  },
  showLabels = true
}) => {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  useEffect(() => {
    // Animate in on first render
    const timer = setTimeout(() => setAnimateIn(true), 100);
    return () => clearTimeout(timer);
  }, []);
  const handleInteraction = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    let clientX, clientY;
    if ('touches' in e) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const x = (clientX - rect.left) / rect.width * 100;
    const y = (clientY - rect.top) / rect.height * 100;
    setPosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  };
  const getMoodLabel = (x: number, y: number): string => {
    if (y < 30 && x > 70) return 'Energiek en positief';
    if (y < 30 && x < 30) return 'Gespannen en alert';
    if (y > 70 && x < 30) return 'Rustig maar somber';
    if (y > 70 && x > 70) return 'Ontspannen en tevreden';
    if (y > 40 && y < 60 && x > 40 && x < 60) return 'Neutraal';
    // Additional mood labels for more granularity
    if (y < 40 && x > 40 && x < 60) return 'Energiek';
    if (y > 60 && x > 40 && x < 60) return 'Rustig';
    if (x < 40 && y > 40 && y < 60) return 'Somber';
    if (x > 60 && y > 40 && y < 60) return 'Positief';
    return 'Gemengd';
  };
  const handleClick = () => {
    // Capture mood on click
    const moodLabel = getMoodLabel(position.x, position.y);
    if (onCapture) {
      onCapture({
        x: position.x,
        y: position.y,
        label: moodLabel
      });
    }
  };
  // Calculate circle size based on position (center is largest)
  const calculateSize = (x: number, y: number) => {
    const distanceFromCenter = Math.sqrt(Math.pow(x - 50, 2) + Math.pow(y - 50, 2)) / 70;
    return Math.max(0.7, 1 - distanceFromCenter);
  };
  // Size mapping based on prop
  const sizeMap = {
    small: 'w-48 h-48 sm:w-56 sm:h-56',
    medium: 'w-64 h-64 sm:w-72 sm:h-72',
    large: 'w-72 h-72 sm:w-96 sm:h-96'
  };
  const sphereSize = calculateSize(position.x, position.y);
  const diameter = 16 * sphereSize; // Base size is 16rem (256px)
  return <div className={`relative ${sizeMap[size]} transition-opacity duration-500 ${animateIn ? 'opacity-100' : 'opacity-0'}`} data-id="element-620">
      <div className="relative w-full h-full border-2 border-dashed border-gray-300 rounded-full cursor-pointer touch-manipulation" onMouseMove={handleInteraction} onTouchMove={handleInteraction} onMouseDown={() => setIsDragging(true)} onTouchStart={() => setIsDragging(true)} onMouseUp={() => {
      setIsDragging(false);
      handleClick();
    }} onTouchEnd={() => {
      setIsDragging(false);
      handleClick();
    }} data-id="element-621">
        {/* Quadrant guides */}
        <div className="absolute top-1/2 left-0 w-full h-px bg-gray-200" data-id="element-622"></div>
        <div className="absolute top-0 left-1/2 w-px h-full bg-gray-200" data-id="element-623"></div>
        {/* Mood sphere */}
        <div className={`absolute bg-white border-2 border-gray-400 rounded-full transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 ease-out ${isDragging ? 'shadow-md' : ''}`} style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        width: `${diameter}px`,
        height: `${diameter}px`
      }} data-id="element-624"></div>
        {/* Pulse animation when dragging */}
        {isDragging && <div className="absolute bg-gray-200 rounded-full transform -translate-x-1/2 -translate-y-1/2 animate-ping opacity-30" style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        width: `${diameter * 1.5}px`,
        height: `${diameter * 1.5}px`
      }} data-id="element-625"></div>}
        {/* Current mood label */}
        {showLabels && <div className="absolute bottom-0 left-0 right-0 text-center -mb-10 text-sm text-gray-600" data-id="element-626">
            {getMoodLabel(position.x, position.y)}
          </div>}
        {/* Axis labels */}
        {showLabels && <>
            <span className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-8 text-sm text-gray-600" data-id="element-627">
              Energiek
            </span>
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-8 text-sm text-gray-600" data-id="element-628">
              Rustig
            </span>
            <span className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-8 text-sm text-gray-600" data-id="element-629">
              Somber
            </span>
            <span className="absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-8 text-sm text-gray-600" data-id="element-630">
              Blij
            </span>
          </>}
        {/* Quadrant labels */}
        {showLabels && <>
            <span className="absolute top-1/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2 text-xs text-gray-500" data-id="element-631">
              Gespannen
            </span>
            <span className="absolute top-1/4 right-1/4 transform translate-x-1/2 -translate-y-1/2 text-xs text-gray-500" data-id="element-632">
              Energiek & positief
            </span>
            <span className="absolute bottom-1/4 left-1/4 transform -translate-x-1/2 translate-y-1/2 text-xs text-gray-500" data-id="element-633">
              Somber & moe
            </span>
            <span className="absolute bottom-1/4 right-1/4 transform translate-x-1/2 translate-y-1/2 text-xs text-gray-500" data-id="element-634">
              Ontspannen
            </span>
          </>}
      </div>
    </div>;
};
export default MoodSphere;